import React, { useEffect, useState } from 'react'
import { FcViewDetails } from 'react-icons/fc';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import ButtonComponent from '../../components/ButtonComponent';
import InputComponent from '../../components/InputComponent';
import Layout from '../../components/Layout/Layout';
import { SelectItemsProps } from "../../components/Select2Component";
import SelectComponent from "../../components/SelectComponent";
import TableComponent from '../../components/TableComponent';
import RoutesObject from '../../helpers/Routes';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { Parameter } from '../../models/Parameter';
import { Tooltip } from '@mui/material';
import { AuditLogService } from '../../services/AuditLogService';
import { AuditDTO } from '../../models/AuditLog/AuditDTO';

function AuditLogIndex() {
    const intl = useIntl()
    const pageSizes: number[] = [5, 10, 15, 30];
    const [data, setData] = useState<AuditDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const navigate = useNavigate();
    const service = new AuditLogService();

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 10));

    const [goToPage, setGoToPage] = useState<number>();

    const [searchType, setSearchType] = useState<string>(Utilities.LoadParameterFromURLQuery("searchType", "string", ""));
    const [tableName, setTableName] = useState<string>(Utilities.LoadParameterFromURLQuery("tableName", "string", ""));
    const [tableNamesList, setTableNamesList] = useState<SelectItemsProps[]>([]);
    const [startDateSearch, setStartDateSearch] = useState<string>(Utilities.LoadParameterFromURLQuery("startDateSearch", "date", ""));
    const [endDateSearch, setEndDateSearch] = useState<string>(Utilities.LoadParameterFromURLQuery("endDateSearch", "date", ""));

    useEffect(() => {
        fetchTableNames();
    }, []);

    const [typesList, setTypesList] = useState<SelectItemsProps[]>([]);
    
    useEffect(()=>{
        fetchTypes();
    }, []);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => {
                    return (
                        <Tooltip
                            title="Ver Detalhes"
                            aria-label="Ver Detalhes"
                            enterNextDelay={500}
                            enterDelay={500}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer"
                                }}
                                onClick={() => { navigate(`${RoutesObject.auditLogs.details}/${props.cell.row.original.id}`) }}
                            >
                                <FcViewDetails className="icons" />
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                Header: "Tipo",
                accessor: "type",
                width: 33,
            },
            {
                Header: "Nome da Tabela",
                accessor: "tableName",
                width: 33,
            },
            {
                Header: "Data",
                accessor: "dateString",
                width: 33,
            },
        ],
        [],
    );

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
            Utilities.AddParamToQueryIfValue("searchType", "string", searchType),
            Utilities.AddParamToQueryIfValue("tableName", "string", tableName),
            Utilities.AddParamToQueryIfValue("startDateSearch", "date", startDateSearch),
            Utilities.AddParamToQueryIfValue("endDateSearch", "date", endDateSearch),

        ]);
        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder", "string", sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }
        navigate({
            pathname: "/auditLogs",
            search: searchUrl,
        });

        setLoading(true);
        var response = await service.GetAllAuditLog(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setCurrentPage(1);
            setGoToPage(1);
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }
        if (response.items.length === 0) {
            setCurrentPage(1);
            setGoToPage(1);
            Toast.Show("success", "Não existe nenhum histórico com esses dados");
        }
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0)

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("type", searchType));
        parameters.push(new Parameter("startDate", startDateSearch));
        parameters.push(new Parameter("endDate", endDateSearch));
        parameters.push(new Parameter("tableName", tableName));

        return parameters;
    };

    const onRemoveFiltersClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        setSearchType("");
        setTableName("");
        setStartDateSearch("");
        setEndDateSearch("");
        setReloadSearch(true);
    }

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    };

    const onButtonClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    }

    const fetchTableNames = async () => {
        var responseGetTableNames = await service.GetAuditTableNames();
        if (responseGetTableNames.success === false) {
            Toast.Show("error", responseGetTableNames.message);
            return;
        }
        var items: SelectItemsProps[] = [];
        responseGetTableNames.obj.forEach((s) => {
            items.push({
                label: s.tableName,
                value: s.tableName,
            });
        });
        setTableNamesList(items);
    };

    const fetchTypes = async () => {
        var responseGetTypes = await service.GetAuditTypes();
        if (responseGetTypes.success === false) {
            Toast.Show("error", responseGetTypes.message);
            return;
        }

        var items: SelectItemsProps[] = [];
        responseGetTypes.obj.forEach((s) => {
            items.push({
                label: s.type,
                value: s.type,
            });
        });

        setTypesList(items);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
    }, [reloadSearch]);

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <FormattedMessage id="Screens.AuditLog.Title" />
                    </span>
                </div>
                <div className='pageBody'>
                    <Col>
                        <Row>
                            <SelectComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                label={intl.formatMessage({ id: "Screens.AuditLog.Filters.Type" })}
                                selectedItemValue={searchType}
                                items={typesList}
                                onChange={(t: string) => setSearchType(t)}
                            />
                            <SelectComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                label={intl.formatMessage({ id: "Screens.AuditLog.Filters.TableName" })}
                                selectedItemValue={tableName}
                                items={tableNamesList}
                                onChange={(t: string) => setTableName(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"date"}
                                label={intl.formatMessage({ id: "Screens.AuditLog.Filters.StartDate" })}
                                readOnly={false}
                                value={startDateSearch}
                                onEnter={onEnter}
                                onChange={(t: string) => setStartDateSearch(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"date"}
                                label={intl.formatMessage({ id: "Screens.AuditLog.Filters.EndDate" })}
                                readOnly={false}
                                value={endDateSearch}
                                onEnter={onEnter}
                                onChange={(t: string) => setEndDateSearch(t)}
                            />
                        </Row>
                    </Col>
                    <Row>
                        <Col xl={3} lg={3} md={3} sm={12} xs={12}
                            style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                onClick={onButtonClick}
                            />
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                onClick={onRemoveFiltersClick}
                            />
                        </Col>
                    </Row>

                    <div className="pageComponent" style={{ width: "100%" }}>
                        <TableComponent
                            goToPage={goToPage}
                            loading={loading}
                            pageCount={pageCount}
                            columns={columns}
                            manualPagination={true}
                            fetchData={fetchData}
                            pageSizes={pageSizes}
                            initialPage={page}
                            initialPageSize={pageSize}
                            initialSortBy={initialSortBy}
                            data={data}
                            dontOrderFirstCollumn={true}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default AuditLogIndex